import React from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  // Reveal fractional rupiah when the difference is non-integer (strict balance check).
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

const ImportStokDialog = ({ 
  open, 
  onClose, 
  detail,
  selectedPeriod,
  onRefresh
}) => {
  if (!open || !detail?.transfers) return null;

  const handleCancelImportStok = async (transfer) => {
    if (!confirm('Yakin ingin membatalkan Import Stok ini? Penjualan di periode asal dan pembelian di periode tujuan akan dihapus. Persediaan akan berkurang.')) {
      return;
    }
    try {
      await api.delete(`/period-transfers/${transfer.id}`);
      toast.success('Import Stok berhasil dibatalkan. Persediaan telah dikurangi.');
      if (onRefresh) await onRefresh();
      onClose();
    } catch (error) {
      console.error('Failed to cancel import stok:', error);
      toast.error('Gagal membatalkan Import Stok: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
      data-testid="import-stok-dialog-overlay"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden" 
        onClick={(e) => e.stopPropagation()}
        data-testid="import-stok-dialog"
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                Import Stok - {detail.branchName}
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {detail.transfers.length} transaksi Import Stok
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
              data-testid="import-stok-dialog-close"
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <table className="w-full" data-testid="import-stok-table">
            <thead className="bg-orange-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tanggal</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Deskripsi</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Berat (gr)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Jumlah Item</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Total Nilai</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {detail.transfers.map((transfer, idx) => (
                <tr key={transfer.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-orange-50'}>
                  <td className="px-4 py-3 text-slate-700">
                    {new Date(transfer.timestamp).toLocaleDateString('id-ID')}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {transfer.description || 'Import Stok'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {(transfer.total_weight || 0).toFixed(2)} gr
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {transfer.entity_count || transfer.entity_ids?.length || 0} item
                  </td>
                  <td className="px-4 py-3 text-right text-orange-600 font-semibold">
                    {formatCurrency(transfer.total_amount || 0)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleCancelImportStok(transfer)}
                      className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      data-testid={`import-stok-cancel-${transfer.id}`}
                    >
                      Batalkan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-orange-100">
              <tr>
                <td colSpan="2" className="px-4 py-3 text-left font-bold text-slate-800">Total</td>
                <td className="px-4 py-3 text-right font-bold text-slate-700">
                  {detail.transfers.reduce((sum, t) => sum + (t.total_weight || 0), 0).toFixed(2)} gr
                </td>
                <td className="px-4 py-3 text-right font-bold text-slate-700">
                  {detail.transfers.reduce((sum, t) => sum + (t.entity_count || t.entity_ids?.length || 0), 0)} item
                </td>
                <td className="px-4 py-3 text-right font-bold text-orange-600">
                  {formatCurrency(detail.transfers.reduce((sum, t) => sum + (t.total_amount || 0), 0))}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        
        <div className="p-4 border-t flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700"
            data-testid="import-stok-dialog-close-btn"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportStokDialog;
